import Field from './Field'
import FieldRule from './FieldRule'
import SchemaBuilder from './SchemaBuilder'
import {substringIndex} from '@utils'

/*
    Converts user validation rules into internal format.
*/
export default class RulesParser
{
    constructor( rules = [], fieldsPrefix = null, defaultScenarios = ['default'], defaultWhen = true, defaultSkipOnEmpty = true, defaultEmptyOn = undefined ) {
        this.load({}) // init schema

        this.setFieldsPrefix(fieldsPrefix)

        this.setDefaultScenarios(defaultScenarios)

        this.setDefaultWhen(defaultWhen)

        this.setDefaultSkipOnEmpty(defaultSkipOnEmpty)

        this.setDefaultEmptyOn(defaultEmptyOn)

        this.parse(rules)
    }

    parse( rules ) {
        if( rules instanceof Function ) {
            // schema builder
            this._parse(
                rules.bind({
                    rules: SchemaBuilder.rules
                })( SchemaBuilder.create, SchemaBuilder.rules, SchemaBuilder )
            )
        }
        else if ( Object.keys(rules).length > 0 ) {
            this._parse(rules)
        }

        return this
    }

    load( schema ) {
        this.schema = schema
        this.schemaOrder = Object.keys(schema)
        return this
    }

    merge( schema ) {
        this.schema = Object.assign({}, this.schema, schema)

        Object.keys(schema).forEach( key => {
            if( !inArray(key, this.schemaOrder) )
                this.schemaOrder.push(key)
        })

        return this
    }

    setDefaultScenarios( scenarios ) {
        this._defaultScenarios = scenarios
        return this
    }

    setDefaultWhen( when ) {
        this._defaultWhen = when
        return this
    }

    setDefaultSkipOnEmpty( skipOnEmpty ) {
        this._defaultSkipOnEmpty = skipOnEmpty
        return this
    }

    setDefaultEmptyOn( callback ) {
        this._defaultEmptyOn = callback
        return this
    }

    setFieldsPrefix( fieldsPrefix ) {
        this._fieldsPrefix = fieldsPrefix
        return this
    }

    asSchema() {
        return this.schema
    }

    getSchemaOrder() {
        return this.schemaOrder
    }

    ///////////////////////////////////////////////////

    _parse( rules ) {
        // each specific line from the rules
        // it contains many validations per fields
        Object.keys(rules).forEach( ruleSet => {
            let fieldRules = rules[ruleSet]

            // extract the fields array
            let fields = Array.isArray(fieldRules[0])
                            ? fieldRules[0]
                            : fieldRules[0].split(',')

            // trim field names
            fields.map( field => {
                if(
                    field instanceof RegExp ||
                    field === null ||
                    field === undefined
                ) {
                    return field
                }

                return field.trim()
            })

            fieldRules.shift() // remove the first 'fields' element
            //

            // convert user described field validations into proper internal format
            let validations = this._parseRules(fields, fieldRules)

            // extract scenarios for the current validation set of rules
            let scenarios = this._parseScenarios(validations)
            delete validations.scenario // clear scenarios from validators

            // handle 'and' and 'or' validators
            let andValidations = false
            if( validations.hasOwnProperty(Field.RULE_OPERATOR_AND) ) {
                andValidations = this._parseRules(fields, validations.and.params.conditions)
                delete validations.and
            }

            let orValidations = false
            if( validations.hasOwnProperty(Field.RULE_OPERATOR_OR) ) {
                orValidations = this._parseRules(fields, validations.or.params.conditions)
                delete validations.or
            }
            //

            // fill the final `schema` object
            this._handleParsedFieldRules(fields, validations, scenarios)

            if(false !== andValidations)
                this._handleParsedFieldRules(fields, andValidations, scenarios, true)

            if(false !== orValidations)
                this._handleParsedFieldRules(fields, orValidations, scenarios, true, Field.RULE_OPERATOR_OR)
            //
        })

        return this
    }

    _handleParsedFieldRules( fields, validations, scenarios, isNested = false, logicalOperator = Field.RULE_OPERATOR_AND )
    {
        // check for forbidden nested validators
        if( isNested ) {
            let forbiddenNestedValidators = [Field.RULE_OPERATOR_AND, Field.RULE_OPERATOR_OR, 'scenario']

            forbiddenNestedValidators.forEach ( forbiddenValidator => {
                if( validations.hasOwnProperty(forbiddenValidator) ) {
                    console.error('[Validator]: Rule "'+ forbiddenValidator +'" is not supported in sub rules.')
                    delete validations[forbiddenValidator]
                }
            })
        }

        // extract `when` validator for further apply
        let when = this._defaultWhen
        if( validations.hasOwnProperty('when') ) {
            when = validations.when.params.condition
            delete validations.when // clear when from validators
        }

        // extract `skipOnEmpty` validator for further apply
        let skipOnEmpty = this._defaultSkipOnEmpty
        if( validations.hasOwnProperty('skipOnEmpty') ) {
            skipOnEmpty = validations.skipOnEmpty.params.condition
            delete validations.skipOnEmpty // clear skipOnEmpty from validators
        }

        // extract `emptyOn` validator for further apply
        let emptyOn = this._defaultEmptyOn
        if( validations.hasOwnProperty('emptyOn') ) {
            emptyOn = validations.emptyOn.params.callback
            delete validations.emptyOn // clear emptyOn from validators
        }

        // foreach all parsed field rules
        Object.keys(validations).forEach( validatorName => {
            // apply 'when' to each validation if current validation do not have its own

            if( ! Array.isArray(validations[validatorName]) ) {
                return // skip "each"
            }

            validations[validatorName].forEach( validationRule => {
                if( ! validationRule.hasWhen() )
                    validationRule.setWhen(when)

                if( validationRule.skipOnEmpty === undefined )
                    validationRule.skipOnEmpty = skipOnEmpty

                if( ! validationRule.hasEmptyOn() && emptyOn !== undefined )
                    validationRule.setEmptyOn(emptyOn)
            })
        })

        // merge local parsed field validations list with the final `schema`
        fields.forEach( field => {
            // calc field name
            let fieldName

            if( field instanceof RegExp || field === null || field === undefined /* $() */ ) {
                fieldName = field
            } else {
                if( field.substr(0, 2) === '::' ) {
                    fieldName = field.substr(2)
                } else {
                    fieldName = field

                    let parentSteps = (field.match(new RegExp("<", "g")) || []).length

                    let prefix = this._fieldsPrefix

                    if( prefix !== null ) {
                        if( parentSteps > 0 ) {
                            prefix = this._getParentFieldName(prefix, false, -parentSteps)
                            fieldName = field.substr(parentSteps + 2/* :: */)
                        }

                        if( false !== prefix ) {
                            fieldName = prefix + '.' + fieldName
                        }
                    }

                    if( false === prefix || (parentSteps > 0 && this._fieldsPrefix === null) ) {
                        // do not show warning in case target parent is the root
                        if( false === prefix && parentSteps === this._fieldsPrefix.split('.').length ) {
                            return
                        }

                        console.error(`[Validator]: Invalid parent steps selector. There are no "${parentSteps}" parent steps for field: "${field}".`)
                    }
                }
            }

            //

            if( typeof this.schema[fieldName] === 'undefined' ) {
                // create new schema field record
                this.schema[fieldName] = new Field(field)
                this.schemaOrder.push(fieldName)
            }

            let hasEachRule = validations.hasOwnProperty('each')

            // handle `each`
            if( hasEachRule ) {
                this.merge(new RulesParser(validations.each.params.rules, fieldName, scenarios, when, skipOnEmpty, emptyOn).asSchema())
                delete validations.each
            }

            if( fieldName === undefined || fieldName === null ) {
                delete this.schema[fieldName]
                if( ! hasEachRule ) {
                    console.error('[Validator]: Please provide some fields to rule `fields()`.')
                }
            } else {
                this.schema[fieldName].addRules(scenarios, logicalOperator, validations)
            }
        })
        //
    }

    _getParentFieldName(fieldName, otherwise = '', parentLevel = -1) {
        let parentKey = substringIndex(fieldName, '.', parentLevel)

        if( parentKey === '' ) {
            return otherwise
        }

        return parentKey
    }

    _parseRules( fields, ruleList )
    {
        let validations = {}

        // For each rule ex.: ['between', 1, 100] in the rules list ex.: ['msisdn', ['between', 1, 100], ['in', [1, 2]], ['regex', '/[0-9]/']],
        Object.keys(ruleList).forEach( index => {
            let validator = ruleList[index]
            let [validatorName, validatorParams, validatorOptions] = validator

            // merge validator options

            let rule = new FieldRule(validatorName, validatorOptions).setParams(validatorParams) // apply user defined options

            let specialRules = ['and', 'or', 'scenario', 'when', 'emptyOn', 'skipOnEmpty', 'each']

            if ( inArray(validatorName, specialRules) )
            {
                validations[validatorName] = rule
                return
            }

            if ( validations[validatorName] === undefined ) {
                validations[validatorName] = []
            }

            validations[validatorName].push(rule)
        })

        return validations
    }

    _parseScenarios( validations ) {
        let scenarios = this._defaultScenarios
        if( validations.hasOwnProperty('scenario') ) {
            scenarios = validations.scenario.params.data

            if( ! Array.isArray(scenarios))
                scenarios = [scenarios]
        }
        return scenarios
    }
}
